import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCachedGet } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { fechaSolo } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, ArrowRight, FolderKanban } from "lucide-react";

import FechaInput from "@/components/FechaInput";
export default function Colecciones() {
  const { user } = useAuth();
  const { data: cols, refetch: load } = useCachedGet("/colecciones");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", temporada: "", responsable: "", fecha_inicio: "", fecha_termino: "" });
  const [busy, setBusy] = useState(false);

  const crear = async () => {
    setBusy(true);
    try {
      await api.post("/colecciones", form);
      toast.success("Colección creada");
      setOpen(false);
      setForm({ nombre: "", temporada: "", responsable: "", fecha_inicio: "", fecha_termino: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Colecciones</h1>
          <p className="text-slate-500 mt-1">Cada colección agrupa los modelos de una temporada.</p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-lg" data-testid="new-coleccion-button"><Plus size={16} className="mr-1" /> Nueva colección</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nueva colección</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Nombre</Label>
                  <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Primavera-Verano 2026" data-testid="coleccion-nombre-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Temporada</Label>
                  <Input value={form.temporada} onChange={(e) => setForm({ ...form, temporada: e.target.value })} placeholder="Ej. PV26" data-testid="coleccion-temporada-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Responsable</Label>
                  <Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} placeholder="Opcional" data-testid="coleccion-responsable-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Fecha de inicio</Label>
                    <FechaInput value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} data-testid="coleccion-inicio-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Fecha de término</Label>
                    <FechaInput value={form.fecha_termino} onChange={(e) => setForm({ ...form, fecha_termino: e.target.value })} data-testid="coleccion-termino-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={crear} disabled={busy || !form.nombre || !form.temporada} data-testid="coleccion-save-button">Crear</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!cols ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : cols.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">No hay colecciones todavía.</Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cols.map((c) => (
            <Link key={c.id} to={`/colecciones/${c.id}`} className="block h-full" data-testid={`coleccion-card-${c.id}`}>
              <Card className="h-full flex flex-col p-6 border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all group">
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                    <FolderKanban size={20} />
                  </div>
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">{c.temporada}</span>
                </div>
                <h3 className="font-head text-lg font-bold tracking-tight mt-4">{c.nombre}</h3>
                <div className="text-sm text-slate-500 mt-1">{c.num_modelos} modelos</div>
                {/* Siempre una línea de fechas para que todas las tarjetas midan lo mismo */}
                <div className="text-xs text-slate-400 mt-1">
                  {c.fecha_inicio || c.fecha_termino ? `${fechaSolo(c.fecha_inicio)} → ${fechaSolo(c.fecha_termino)}` : "Sin fechas definidas"}
                </div>
                <div className="mt-auto pt-4 flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                  Abrir <ArrowRight size={15} />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
